
import reportsAPI from '@/logic/api/reports';
import EazyHeading from '@/components/EazyHeading.vue';
import UserRow from '@/components/UserRow.vue';
import BarChart from '@/components/Charts/BarChart.vue';
import getMeetingDateAndTime from '@/logic/helpers/dates/getMeetingDateAndTime';
import router from '@/router';

export default {
  name: 'VAReport',

  components: {
    EazyHeading,
    UserRow,
    BarChart,
  },

  data(): any {
    return {
      router,
      candidateId: this.$route.query.candidateId,
      virtualAssessmentReportPath: this.$route.query.virtualAssessmentReportPath,
      reportId: this.$route.query.reportId,
      assessmentDate: this.$route.query.assessmentDate,
      forCompany: this.$route.query.forCompany,
      fetchingReport: true,
      fetchingCandidate: true,
      uploadingVR: false,
      VAReportModel: Object,
      candidate: {
        first_name: '',
        surname: '',
      },
      someDataMissingForPublish: false,
      chartType: 'bar',
      getMeetingDateAndTime,
      VRPublished: false,
      publishAttemptedWithSomethingNotCompleted: false,
      that: this,
      uploadTriggered: false,
      uploadTriggeredWithPublishedStatus: false,
      autoSaveInterval: null,
    };
  },

  computed: {
    showCommentsError(): boolean {
      return this.uploadTriggered && this.publishAttemptedWithSomethingNotCompleted;
    },
    isReadonly(): boolean {
      return (
        this.VAReportModel.status === 'published' ||
        this.VAReportModel.status === 'accepted' ||
        this.VAReportModel.status === 'in_review' ||
        this.VRPublished
      );
    },

    canCreateDraft(): boolean {
      return !this.isReadonly;
    },
  },

  created(): void {
    this.loadDetailedReport();
    this.loadCandidate();
    setTimeout(() => {
      this.startDraftAutoSave();
    }, 10000);
  },
  beforeDestroy(): void {
    this.stopDraftAutoSave();
  },

  methods: {
    startDraftAutoSave(): void {
      if (!this.isReadonly && !this.autoSaveInterval) {
        this.autoSaveInterval = setInterval(() => {
          this.uploadReport('draft', true);
        }, 30000);
        this.$message(this.$t('draftSaved'));
      }
    },
    stopDraftAutoSave(): void {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    },
    async loadDetailedReport(): Promise<void> {
      const resp = await reportsAPI.getVirtualAssessmentReport(this.virtualAssessmentReportPath);

      if (resp.ok) {
        const data = await resp.json();
        this.VAReportModel = data;
        const pathSplitted = this.virtualAssessmentReportPath.split('/');
        this.VAReportModel.id = pathSplitted[pathSplitted.length - 1];
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
      }

      this.fetchingReport = false;
    },

    async loadCandidate(): Promise<void> {
      const resp = await reportsAPI.getCandidate(this.candidateId);

      if (resp.ok) {
        const data = await resp.json();
        this.candidate = data;
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.fetchingCandidate = false;
    },

    checkIfValidationError(): boolean {
      let validationError;
      const finalFits = Object.values(this.VAReportModel.fits);

      if (this.VAReportModel.comments === '') validationError = true;
      else if (
        this.VAReportModel.properties.some((prop) => {
          return prop.comments === '' || !prop.fits;
        })
      ) {
        validationError = true;
      } else if (
        finalFits.some((fit: any) => {
          return !fit.fits;
        })
      ) {
        validationError = true;
      } else {
        validationError = false;
      }

      return validationError;
    },

    async uploadReport(status: string, isAutosave: boolean): Promise<void> {
      this.uploadTriggered = true;
      if (status !== 'draft') {
        this.stopDraftAutoSave();
        this.checkIfValidationError()
          ? (this.publishAttemptedWithSomethingNotCompleted = true)
          : (this.publishAttemptedWithSomethingNotCompleted = false);
        if (this.publishAttemptedWithSomethingNotCompleted) return;
      }
      if (!isAutosave) {
        this.uploadingVR = true;
      }

      const resp = await reportsAPI.uploadVirtualReport(this.VAReportModel, status);

      if (resp.ok) {
        status === 'published' || status === 'in_review'
          ? this.$message.success(this.$t('reportPublished'))
          : this.$message(this.$t('draftSaved'));
        if (status === 'published' || status === 'in_review') this.VRPublished = true;
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      if (!isAutosave) {
        this.uploadingVR = false;
      }
      this.publishAttemptedWithSomethingNotCompleted = false;
    },

    capitalizeFirstLetter(string: string): string {
      if (this.VAReportModel.tool) return string[0].toUpperCase() + string.slice(1);
    },

    prepareChartData(competence: Record<string, unknown>): Record<string, unknown> {
      return {
        labels: competence.labels,
        datasets: [
          {
            data: competence.data,
            backgroundColor: competence.colors,
            lineTension: 0,
          },
        ],
      };
    },

    chartOptions(that: any, graphIdx: number): unknown {
      const options = {
        onClick: (event, chartEl) => {
          if (that.isReadonly) return;

          const columnIdx = chartEl[0]._index;
          that.changeColorOfGraphColumnAndAdjustFlag(graphIdx, columnIdx);
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                precision: 0,
                suggestedMax: 9,
                max: 9,
              },
            },
          ],
        },
      };

      if (that.chartType === 'pie') delete options.scales;

      return options;
    },

    changeColorOfGraphColumnAndAdjustFlag(graphIdx: number, columnIdx: number): void {
      const grey = '#b2b7b4';
      const yellow = '#e6a23c';
      const red = '#f56c6c';
      let currentColor = this.VAReportModel.properties[graphIdx].colors[columnIdx];
      let nextColor;

      if (currentColor === grey) {
        nextColor = yellow;
      } else if (currentColor === yellow) {
        nextColor = red;
      } else if (currentColor === red) {
        nextColor = grey;
      }

      this.VAReportModel.properties[graphIdx].colors[columnIdx] = nextColor;
      this.adjustFlag(graphIdx);
      this.$forceUpdate();
    },

    adjustFlag(graphIdx: number): void {
      const grey = '#b2b7b4';
      const yellow = '#e6a23c';
      const red = '#f56c6c';
      const graph = this.VAReportModel.properties[graphIdx];

      if (
        graph.colors.some((col) => {
          return col === yellow;
        })
      ) {
        graph.fits = '2';
      }

      if (
        this.VAReportModel.properties[graphIdx].colors.some((col) => {
          return col === red;
        })
      ) {
        this.VAReportModel.properties[graphIdx].fits = '3';
      }

      if (
        graph.colors.every((col) => {
          return col === grey;
        })
      ) {
        this.VAReportModel.properties[graphIdx].fits = '1';
      }
    },
  },
};
